import { SalonIcon } from "./SalonIcon";
import { SalonsDrawer } from "./SalonsDrawer";
import { ActionButton, ActionToolbar, OverflowMenu } from "../adaptive/actions";
import { useEffect, useMemo, useState } from "react";
import type { PlatformApiClient } from "../api";
import { PlatformApiError } from "../api";
import type {
  SalonCustomer360,
  SalonCustomerImportMapping,
  SalonCustomerImportPreview,
  SalonCustomerList,
  SalonCustomerListItem,
  SalonCustomerSegment,
  SalonCustomerSort,
} from "./types";

type CustomerView = "summary" | "list" | "segments";
type Customer360Tab = "summary" | "appointments" | "conversations" | "sales" | "profile" | "activity";

function errorText(error: unknown): string {
  if (error instanceof PlatformApiError) return error.message;
  return "No se pudieron cargar los clientes.";
}

async function fileToBase64(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = "";
  for (let index = 0; index < bytes.length; index += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(index, index + 0x8000));
  }
  return window.btoa(binary);
}

function downloadBase64(fileName: string, mimeType: string, contentBase64: string) {
  const binary = window.atob(contentBase64);
  const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: mimeType }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

function tagsFromText(value: string): readonly string[] {
  const seen = new Map<string, string>();
  for (const raw of value.split(/[;,|]/)) {
    const tag = raw.trim();
    if (tag && !seen.has(tag.toLocaleLowerCase("es"))) {
      seen.set(tag.toLocaleLowerCase("es"), tag);
    }
  }
  return [...seen.values()];
}

function preferencesFromText(value: string): Readonly<Record<string, string | number | boolean | null>> {
  const parsed: unknown = value.trim() ? JSON.parse(value) : {};
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    throw new Error("Preferencias debe ser un objeto JSON.");
  }
  for (const entry of Object.values(parsed as Record<string, unknown>)) {
    if (entry !== null && !["string", "number", "boolean"].includes(typeof entry)) {
      throw new Error("Cada preferencia debe ser texto, número, booleano o null.");
    }
  }
  return parsed as Readonly<Record<string, string | number | boolean | null>>;
}

function initials(value: string | null): string {
  const parts = (value ?? "Cliente").trim().split(/\s+/).filter(Boolean).slice(0, 2);
  return parts.map((part) => part[0]?.toUpperCase() ?? "").join("") || "?";
}

function dateTime(value: string | null | undefined): string {
  if (!value || !Number.isFinite(Date.parse(value))) return "—";
  return new Intl.DateTimeFormat("es-AR", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(new Date(value));
}

function relativeDate(value: string | null | undefined): string {
  if (!value || !Number.isFinite(Date.parse(value))) return "Sin actividad";
  const diff = Date.now() - Date.parse(value);
  const days = Math.max(0, Math.round(diff / 86_400_000));
  if (days === 0) return "Hoy";
  if (days === 1) return "Ayer";
  if (days < 30) return `Hace ${days} días`;
  return dateTime(value);
}

function money(value: number, currency: string): string {
  return new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value / 100);
}

function collectedLabel(item: SalonCustomerListItem): string {
  if (item.salesByCurrency.length === 0) return "—";
  return item.salesByCurrency
    .map((entry) => money(entry.collectedAmountMinor, entry.currency))
    .join(" · ");
}

function channelLabel(channel: string | null | undefined): string {
  if (!channel) return "—";
  switch (channel.toLowerCase()) {
    case "whatsapp": return "WhatsApp";
    case "voice": return "Voice";
    case "instagram": return "Instagram";
    case "messenger": return "Messenger";
    default: return channel;
  }
}

function commercialStateLabel(value: string): string {
  switch (value) {
    case "active_upcoming":
    case "scheduled": return "Con turno";
    case "no_show_risk": return "Riesgo no-show";
    case "cancelled_only": return "Cancelaciones";
    case "attended": return "Atendido";
    case "new_customer": return "Cliente nuevo";
    case "no_booking": return "Sin turnos";
    case "needs_follow_up": return "A recuperar";
    case "inactive": return "Inactivo";
    default: return "Sin clasificar";
  }
}

function appointmentStatusLabel(value: string): string {
  switch (value) {
    case "confirmed": return "Confirmado";
    case "pending": return "Pendiente";
    case "completed": return "Completado";
    case "cancelled": return "Cancelado";
    case "no_show": return "No asistió";
    default: return value;
  }
}

function customer360Segment(value: string): { label: string; modifier: string } {
  switch (value) {
    case "scheduled": return { label: "Con turno", modifier: "booking" };
    case "needs_follow_up": return { label: "A recuperar", modifier: "recover" };
    case "attended": return { label: "Atendido", modifier: "recurrent" };
    case "inactive": return { label: "Inactivo", modifier: "recover" };
    default: return { label: commercialStateLabel(value), modifier: "neutral" };
  }
}

function segmentLabel(item: SalonCustomerListItem): { label: string; modifier: string } {
  if (item.segment === "recurrent") return { label: "Recurrente", modifier: "recurrent" };
  if (item.segment === "recover") return { label: "A recuperar", modifier: "recover" };
  if (item.nextAppointment) return { label: "Con turno", modifier: "booking" };
  if (item.summary.commercialState === "no_show_risk") return { label: "Riesgo no-show", modifier: "urgent" };
  if (["new_customer", "no_booking"].includes(item.summary.commercialState)) return { label: "Nuevo", modifier: "lead" };
  return { label: commercialStateLabel(item.summary.commercialState), modifier: "neutral" };
}

function CustomerTable({
  snapshot,
  onOpen,
}: {
  snapshot: SalonCustomerList;
  onOpen: (customerId: string) => void;
}) {
  if (snapshot.items.length === 0) {
    return <div className="cd-crm-empty-view">
      <SalonIcon name="group_off" className="cd-crm-empty-view__icon" />
      <p className="cd-crm-empty-view__title">Sin clientes en este segmento</p>
      <p className="cd-crm-empty-view__text">Cuando un cliente escriba, reserve o vuelva a interactuar, aparecerá acá.</p>
    </div>;
  }

  return <div className="cd-crm-table-card">
    <table className="cd-crm-table">
      <thead>
        <tr>
          <th>Cliente</th>
          <th>Estado</th>
          <th>Última actividad</th>
          <th>Próximo turno</th>
          <th>Último servicio</th>
          <th>Origen</th>
          <th>Historial</th>
          <th>Cobrado</th>
          <th>Acción</th>
        </tr>
      </thead>
      <tbody>
        {snapshot.items.map((item) => {
          const segment = segmentLabel(item);
          return <tr
            key={item.customer.id}
            className={`cd-crm-row${item.segment === "recover" ? " cd-crm-row--urgent" : ""}`}
            tabIndex={0}
            role="button"
            aria-label={`Abrir ${item.customer.displayName ?? "cliente sin nombre"}`}
            onClick={() => onOpen(item.customer.id)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onOpen(item.customer.id);
              }
            }}
          >
            <td className="cd-crm-row__client" data-label="Cliente">
              <div className="cd-crm-row__identity">
                <div className="cd-crm-row__avatar" aria-hidden="true">{initials(item.customer.displayName)}</div>
                <div>
                  <p className="cd-crm-row__name">{item.customer.displayName ?? "Cliente sin nombre"}</p>
                  {item.customer.phone && <p className="cd-crm-row__phone">{item.customer.phone}</p>}
                  {item.customer.tags.length > 0 && <p className="cd-crm-row__tags">{item.customer.tags.join(" · ")}</p>}
                </div>
              </div>
            </td>
            <td data-label="Estado"><span className={`cd-crm-segment cd-crm-segment--${segment.modifier}`}>{segment.label}</span></td>
            <td className="cd-crm-row__muted" data-label="Actividad">{relativeDate(item.activityAt)}</td>
            <td className="cd-crm-row__muted" data-label="Próximo turno">{item.nextAppointment ? dateTime(item.nextAppointment.startsAt) : "Sin turno"}</td>
            <td className="cd-crm-row__muted" data-label="Último servicio">{item.lastAppointment?.service?.name ?? "—"}</td>
            <td className="cd-crm-row__muted" data-label="Origen">{channelLabel(item.customer.sourceChannel)}</td>
            <td className="cd-crm-row__value" data-label="Historial">{item.summary.totalAppointments ?? "—"} turnos</td>
            <td className="cd-crm-row__value" data-label="Cobrado">{collectedLabel(item)}</td>
            <td className="cd-crm-row__action-hint" data-label="Acción">
              <span className="cd-crm-row__action-label">
                <SalonIcon name="arrow_forward" />
                Ver ficha
              </span>
            </td>
          </tr>;
        })}
      </tbody>
    </table>
  </div>;
}

function SummaryCard({
  count,
  title,
  description,
  tone,
  onClick,
}: {
  count: number;
  title: string;
  description: string;
  tone: string;
  onClick: () => void;
}) {
  return <button type="button" className={`cd-crm-opp-card cd-crm-opp-card--${tone}`} onClick={onClick}>
    <div className="cd-crm-opp-card__top">
      <span className="cd-crm-opp-card__count">{count}</span>
      <SalonIcon name={tone === "recover" ? "replay" : tone === "booking" ? "event" : tone === "urgent" ? "priority_high" : "loyalty"} className="cd-crm-opp-card__icon" />
    </div>
    <p className="cd-crm-opp-card__title">{title}</p>
    <p className="cd-crm-opp-card__desc">{description}</p>
    <span className="cd-crm-opp-card__action">Ver clientes →</span>
  </button>;
}

function CustomersWorkspace({
  organizationId,
  api,
  navigate,
  base,
  routeView,
}: {
  organizationId: string;
  api: PlatformApiClient;
  navigate: (path: string) => void;
  base: string;
  routeView: CustomerView;
}) {
  const [view, setView] = useState<CustomerView>(routeView);
  const [segment, setSegment] = useState<SalonCustomerSegment>("all");
  const [sort, setSort] = useState<SalonCustomerSort>("activity_desc");
  const [searchDraft, setSearchDraft] = useState("");
  const [query, setQuery] = useState("");
  const [snapshot, setSnapshot] = useState<SalonCustomerList | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [authExpired, setAuthExpired] = useState(false);
  const [activity, setActivity] = useState<"all" | "with_activity" | "without_activity">("all");
  const [upcoming, setUpcoming] = useState<"all" | "yes" | "no">("all");
  const [history, setHistory] = useState<"all" | "yes" | "no">("all");
  const [noShow, setNoShow] = useState<"all" | "yes" | "no">("all");
  const [serviceId, setServiceId] = useState("");
  const [professionalId, setProfessionalId] = useState("");
  const [locationId, setLocationId] = useState("");
  const [channel, setChannel] = useState("");
  const [tag, setTag] = useState("");
  const [reloadNonce, setReloadNonce] = useState(0);
  const [createOpen, setCreateOpen] = useState(() => new URLSearchParams(window.location.search).get("create") === "1");
  const [createDraft, setCreateDraft] = useState({
    displayName: "",
    phone: "",
    email: "",
    notes: "",
    tags: "",
  });
  const [importOpen, setImportOpen] = useState(false);
  const [importPayload, setImportPayload] = useState<{ fileName: string; contentBase64: string } | null>(null);
  const [importPreview, setImportPreview] = useState<SalonCustomerImportPreview | null>(null);
  const [importMapping, setImportMapping] = useState<SalonCustomerImportMapping | null>(null);
  const [importDecisions, setImportDecisions] = useState<Record<number, { action: "skip" | "update"; targetCustomerId: string | null }>>({});
  const [commandBusy, setCommandBusy] = useState(false);
  const [commandError, setCommandError] = useState<string | null>(null);
  const [commandNotice, setCommandNotice] = useState<string | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setQuery(searchDraft.trim()), 220);
    return () => window.clearTimeout(timer);
  }, [searchDraft]);

  useEffect(() => {
    setView(routeView);
  }, [routeView]);

  useEffect(() => {
    let current = true;
    setLoading(true);
    setError(null);
    setAuthExpired(false);
    api.salonCustomers(organizationId, { q: query, segment, sort, activity, upcoming, history, noShow, serviceId: serviceId || null, professionalId: professionalId || null, locationId: locationId || null, channel: channel || null, tag: tag || null, limit: 100 })
      .then((next) => {
        if (!current) return;
        setSnapshot(next);
      })
      .catch((cause) => {
        if (!current) return;
        const unauthorized = cause instanceof PlatformApiError && cause.status === 401;
        setAuthExpired(unauthorized);
        setError(unauthorized ? "Tu sesión expiró. Iniciá sesión para recuperar el listado." : errorText(cause));
        setSnapshot(null);
      })
      .finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [api, organizationId, query, segment, sort, activity, upcoming, history, noShow, serviceId, professionalId, locationId, channel, tag, reloadNonce]);

  function openList(nextSegment: SalonCustomerSegment) {
    setSegment(nextSegment);
    setView("list");
    navigate(base + "/customers/list");
  }

  function closeCreate() {
    if (commandBusy) return;
    const dirty = Object.values(createDraft).some((value) => value.trim().length > 0);
    if (dirty && !window.confirm("¿Descartar el nuevo cliente sin guardar?")) return;
    setCreateOpen(false);
    setCreateDraft({ displayName: "", phone: "", email: "", notes: "", tags: "" });
    setCommandError(null);
  }

  function closeImport() {
    if (commandBusy) return;
    if ((importPayload || importPreview) && !window.confirm("¿Descartar la importación preparada sin confirmar?")) return;
    setImportOpen(false);
    setImportPayload(null);
    setImportPreview(null);
    setImportMapping(null);
    setImportDecisions({});
    setCommandError(null);
  }

  async function createCustomer() {
    setCommandBusy(true);
    setCommandError(null);
    try {
      const result = await api.createSalonCustomer(organizationId, {
        displayName: createDraft.displayName.trim() || null,
        phone: createDraft.phone.trim() || null,
        email: createDraft.email.trim() || null,
        notes: createDraft.notes.trim() || null,
        tags: tagsFromText(createDraft.tags),
      });
      setCreateOpen(false);
      setCreateDraft({ displayName: "", phone: "", email: "", notes: "", tags: "" });
      setReloadNonce((value) => value + 1);
      navigate(base + "/customers/" + result.customerId);
    } catch (cause) {
      setCommandError(errorText(cause));
    } finally {
      setCommandBusy(false);
    }
  }

  async function exportCustomers() {
    setCommandBusy(true);
    setCommandError(null);
    try {
      const exported = await api.exportSalonCustomers(organizationId);
      downloadBase64(exported.fileName, exported.mimeType, exported.contentBase64);
      setCommandNotice("Exportados " + exported.exportedCustomers + " clientes.");
    } catch (cause) {
      setCommandError(errorText(cause));
    } finally {
      setCommandBusy(false);
    }
  }

  async function previewImportFile(file: File) {
    setCommandBusy(true);
    setCommandError(null);
    setCommandNotice(null);
    try {
      const payload = { fileName: file.name, contentBase64: await fileToBase64(file) };
      const preview = await api.previewSalonCustomerImport(organizationId, payload);
      setImportPayload(payload);
      setImportPreview(preview);
      setImportMapping(preview.mapping);
      setImportDecisions(Object.fromEntries(
        preview.rows
          .filter((row) => row.duplicateCandidates.length > 0)
          .map((row) => [row.rowNumber, {
            action: "skip" as const,
            targetCustomerId: row.duplicateCandidates[0]?.customerId ?? null,
          }]),
      ));
      setImportOpen(true);
    } catch (cause) {
      setCommandError(errorText(cause));
    } finally {
      setCommandBusy(false);
    }
  }

  async function revalidateImport() {
    if (!importPayload || !importMapping) return;
    setCommandBusy(true);
    setCommandError(null);
    try {
      const preview = await api.previewSalonCustomerImport(organizationId, {
        ...importPayload,
        mapping: importMapping,
      });
      setImportPreview(preview);
      setImportMapping(preview.mapping);
      setImportDecisions(Object.fromEntries(
        preview.rows
          .filter((row) => row.duplicateCandidates.length > 0)
          .map((row) => [row.rowNumber, {
            action: "skip" as const,
            targetCustomerId: row.duplicateCandidates[0]?.customerId ?? null,
          }]),
      ));
    } catch (cause) {
      setCommandError(errorText(cause));
    } finally {
      setCommandBusy(false);
    }
  }

  async function commitImport() {
    if (!importPayload || !importMapping || !importPreview) return;
    setCommandBusy(true);
    setCommandError(null);
    try {
      const decisions = importPreview.rows
        .filter((row) => row.duplicateCandidates.length > 0)
        .map((row) => {
          const decision = importDecisions[row.rowNumber] ?? {
            action: "skip" as const,
            targetCustomerId: row.duplicateCandidates[0]?.customerId ?? null,
          };
          return {
            rowNumber: row.rowNumber,
            action: decision.action,
            targetCustomerId: decision.action === "update" ? decision.targetCustomerId : null,
          };
        });
      const result = await api.commitSalonCustomerImport(organizationId, {
        ...importPayload,
        mapping: importMapping,
        decisions,
      });
      setCommandNotice(
        "Importación: " + result.summary.created + " creados, " +
        result.summary.updated + " actualizados, " +
        result.summary.skipped + " omitidos, " +
        result.summary.failed + " con error.",
      );
      if (result.errorReport) {
        downloadBase64(result.errorReport.fileName, result.errorReport.mimeType, result.errorReport.contentBase64);
      }
      setImportOpen(false);
      setImportPayload(null);
      setImportPreview(null);
      setImportMapping(null);
      setImportDecisions({});
      setReloadNonce((value) => value + 1);
    } catch (cause) {
      setCommandError(errorText(cause));
    } finally {
      setCommandBusy(false);
    }
  }

  return <section className="cd-crm-product cd-crm-product--canon" aria-label="Clientes">
    <header className="cd-page-header">
      <div><h1 className="cd-page-header__title">Clientes</h1><p className="cd-page-header__description">Conocé a tus clientes, consultá su historial y sus próximos turnos.</p></div>
      <ActionToolbar className="cd-crm-commandbar" label="Acciones de clientes">
        <OverflowMenu
          label="Más acciones de clientes"
          actions={[
            {
              key: "import",
              label: "Importar CSV/XLSX",
              disabled: commandBusy,
              onSelect: () => setImportOpen((value) => !value),
            },
            {
              key: "export",
              label: "Exportar clientes",
              disabled: commandBusy,
              onSelect: () => void exportCustomers(),
            },
          ]}
        />
        <ActionButton variant="primary" disabled={commandBusy} onClick={() => { setCommandError(null); setCreateOpen(true); }}>
          <SalonIcon name="add" /> Nuevo cliente
        </ActionButton>
      </ActionToolbar>
    </header>
    <nav className="cd-crm-tabs" aria-label="Vistas del CRM">
      {([
        ["summary", "Resumen"],
        ["list", "Lista"],
        ["segments", "Segmentos"],
      ] as const).map(([key, label]) => <button
        type="button"
        key={key}
        className={`cd-crm-tab${view === key ? " cd-crm-tab--active" : ""}`}
        aria-pressed={view === key}
        onClick={() => {
          setView(key);
          navigate(key === "summary" ? base + "/customers" : base + "/customers/" + key);
        }}
      >{label}</button>)}
    </nav>

    {view === "list" && <div className="cd-crm-toolbar cd-crm-toolbar--lista">
      <div className="cd-agenda-chips" aria-label="Filtros de segmento">
        {([
          ["all", "Todos"],
          ["recurrent", "Recurrentes"],
          ["recover", "A recuperar"],
        ] as const).map(([key, label]) => <button
          key={key}
          type="button"
          className={`cd-agenda-chip${segment === key ? " cd-agenda-chip--active" : ""}`}
          onClick={() => setSegment(key)}
        >
          {label}
          {key !== "all" && <span className="cd-crm-chip-count cd-crm-chip-count--muted">{error || loading ? "—" : snapshot?.counts[key] ?? 0}</span>}
        </button>)}
      </div>

      <div className="cd-crm-search-row">
        <label className="cd-crm-search">
          <SalonIcon name="search" className="cd-crm-search__icon" />
          <input
            type="search"
            className="cd-crm-search__input"
            placeholder="Buscar por nombre, teléfono o email"
            aria-label="Buscar cliente por nombre, teléfono o email"
            autoComplete="off"
            value={searchDraft}
            onChange={(event) => setSearchDraft(event.target.value)}
          />
        </label>
        <div className="cd-crm-list-controls">
          <label className="cd-crm-sort">
            <span className="cd-crm-sort__label">Ordenar por</span>
            <select className="cd-crm-sort__select" value={sort} onChange={(event) => setSort(event.target.value as SalonCustomerSort)}>
              <option value="activity_desc">Última actividad reciente</option>
              <option value="activity_asc">Última actividad antigua</option>
              <option value="next_booking_asc">Próximo turno</option>
              <option value="name_asc">Nombre A-Z</option>
              {snapshot?.criteria.spendSortAvailable && <>
                <option value="spend_desc">Mayor gasto ({snapshot.criteria.spendSortCurrency})</option>
                <option value="spend_asc">Menor gasto ({snapshot.criteria.spendSortCurrency})</option>
              </>}
            </select>
          </label>
          <span className="cd-crm-meta">
            {error ? "Datos no disponibles" : loading ? "Cargando clientes…" : `${snapshot?.pagination.totalEstimated ?? 0} clientes`}
          </span>
        </div>
      </div>

      <details className="cd-ux-filter-disclosure">
        <summary>Más filtros{[
          activity !== "all", upcoming !== "all", history !== "all", noShow !== "all",
          Boolean(serviceId), Boolean(professionalId), Boolean(locationId), Boolean(channel), Boolean(tag),
        ].filter(Boolean).length > 0 ? " · " + [
          activity !== "all", upcoming !== "all", history !== "all", noShow !== "all",
          Boolean(serviceId), Boolean(professionalId), Boolean(locationId), Boolean(channel), Boolean(tag),
        ].filter(Boolean).length + " activos" : ""}</summary>
      <div className="cd-crm-filter-grid" aria-label="Filtros operativos de clientes">
        <select aria-label="Actividad" value={activity} onChange={(event) => setActivity(event.target.value as "all" | "with_activity" | "without_activity")}>
          <option value="all">Toda actividad</option>
          <option value="with_activity">Con actividad</option>
          <option value="without_activity">Sin actividad</option>
        </select>
        <select aria-label="Próximo turno" value={upcoming} onChange={(event) => setUpcoming(event.target.value as "all" | "yes" | "no")}>
          <option value="all">Próximo turno: todos</option>
          <option value="yes">Con próximo turno</option>
          <option value="no">Sin próximo turno</option>
        </select>
        <select aria-label="Historial" value={history} onChange={(event) => setHistory(event.target.value as "all" | "yes" | "no")}>
          <option value="all">Historial: todos</option>
          <option value="yes">Con historial</option>
          <option value="no">Sin historial</option>
        </select>
        <select aria-label="No-show" value={noShow} onChange={(event) => setNoShow(event.target.value as "all" | "yes" | "no")}>
          <option value="all">No-show: todos</option>
          <option value="yes">Con no-show</option>
          <option value="no">Sin no-show</option>
        </select>
        <select aria-label="Servicio" value={serviceId} onChange={(event) => setServiceId(event.target.value)}>
          <option value="">Todos los servicios</option>
          {snapshot?.filterOptions.services.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
        </select>
        <select aria-label="Profesional" value={professionalId} onChange={(event) => setProfessionalId(event.target.value)}>
          <option value="">Todos los profesionales</option>
          {snapshot?.filterOptions.professionals.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
        </select>
        <select aria-label="Local" value={locationId} onChange={(event) => setLocationId(event.target.value)}>
          <option value="">Todos los locales</option>
          {snapshot?.filterOptions.locations.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
        </select>
        <select aria-label="Canal" value={channel} onChange={(event) => setChannel(event.target.value)}>
          <option value="">Todos los canales</option>
          {snapshot?.filterOptions.channels.map((item) => <option key={item} value={item}>{channelLabel(item)}</option>)}
        </select>
        <select aria-label="Etiqueta" value={tag} onChange={(event) => setTag(event.target.value)}>
          <option value="">Todas las etiquetas</option>
          {snapshot?.filterOptions.tags.map((item) => <option key={item} value={item}>{item}</option>)}
        </select>
      </div>
      <ActionButton variant="ghost" onClick={() => {
        setActivity("all"); setUpcoming("all"); setHistory("all"); setNoShow("all");
        setServiceId(""); setProfessionalId(""); setLocationId(""); setChannel(""); setTag("");
      }}>Limpiar filtros avanzados</ActionButton>
      </details>
    </div>}

    {commandError && !createOpen && !importOpen && <div className="cd-crm-command-feedback cd-crm-command-feedback--error">{commandError}</div>}
    {commandNotice && <div className="cd-crm-command-feedback">{commandNotice}</div>}

    {createOpen && <SalonsDrawer
      title="Nuevo cliente"
      eyebrow="Clientes"
      description="Completá los datos del cliente. Después podés sumar detalles desde su ficha."
      onClose={closeCreate}
      busy={commandBusy}
    >
      <div className="cd-crm-create-drawer">
      {commandError && <div className="cd-crm-command-feedback cd-crm-command-feedback--error" role="alert">{commandError}</div>}
      <div className="cd-crm-form-grid">
        <label>Nombre<input value={createDraft.displayName} onChange={(event) => setCreateDraft((current) => ({ ...current, displayName: event.target.value }))} /></label>
        <label>Teléfono<input value={createDraft.phone} onChange={(event) => setCreateDraft((current) => ({ ...current, phone: event.target.value }))} /></label>
        <label>Email<input type="email" value={createDraft.email} onChange={(event) => setCreateDraft((current) => ({ ...current, email: event.target.value }))} /></label>
        <label>Etiquetas<input placeholder="VIP, Color, Recurrente" value={createDraft.tags} onChange={(event) => setCreateDraft((current) => ({ ...current, tags: event.target.value }))} /></label>
        <label className="cd-crm-form-grid__wide">Notas<textarea value={createDraft.notes} onChange={(event) => setCreateDraft((current) => ({ ...current, notes: event.target.value }))} /></label>
      </div>
      <div className="cd-crm-operation-panel__actions cd-crm-create-drawer__footer">
        <button type="button" className="cd-btn-ghost" disabled={commandBusy} onClick={closeCreate}>Cancelar</button>
        <button type="button" className="cd-btn-primary" disabled={commandBusy || ![createDraft.displayName, createDraft.phone, createDraft.email].some((value) => value.trim())} onClick={() => void createCustomer()}>Crear cliente</button>
      </div>
      </div>
    </SalonsDrawer>}

    {importOpen && <SalonsDrawer
      title="Importar clientes"
      eyebrow="Clientes"
      description="Preview → validación → revisión de duplicados → commit."
      width="wide"
      busy={commandBusy}
      onClose={closeImport}
    >
      <div className="cd-crm-import-drawer">
      {commandError && <div className="cd-crm-command-feedback cd-crm-command-feedback--error" role="alert">{commandError}</div>}
      {!importPayload && <label className="cd-crm-import-drop">
        <span>Seleccioná un CSV o XLSX</span>
        <input type="file" accept=".csv,.xlsx" onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) void previewImportFile(file);
        }} />
      </label>}
      {importPreview && importMapping && <>
        <div className="cd-crm-import-summary">
          <strong>{importPreview.summary.totalRows} filas</strong>
          <span>{importPreview.summary.validRows} válidas</span>
          <span>{importPreview.summary.duplicateRows} duplicadas</span>
          <span>{importPreview.summary.invalidRows} con error</span>
        </div>
        <div className="cd-crm-import-mapping">
          {([
            ["displayName", "Nombre"],
            ["phone", "Teléfono"],
            ["email", "Email"],
            ["notes", "Notas"],
            ["tags", "Etiquetas"],
          ] as const).map(([field, label]) => <label key={field}>{label}
            <select value={importMapping[field] ?? ""} onChange={(event) => setImportMapping((current) => current ? ({ ...current, [field]: event.target.value || null }) : current)}>
              <option value="">No importar</option>
              {importPreview.headers.map((header) => <option key={header} value={header}>{header}</option>)}
            </select>
          </label>)}
          <button type="button" className="cd-btn-ghost" disabled={commandBusy} onClick={() => void revalidateImport()}>Revalidar mapeo</button>
        </div>
        <div className="cd-crm-import-table-wrap">
          <table className="cd-crm-import-table">
            <thead><tr><th>Fila</th><th>Cliente</th><th>Contacto</th><th>Estado</th><th>Decisión</th></tr></thead>
            <tbody>{importPreview.rows.slice(0, 100).map((row) => {
              const decision = importDecisions[row.rowNumber];
              return <tr key={row.rowNumber}>
                <td>{row.rowNumber}</td>
                <td>{row.values.displayName ?? "—"}</td>
                <td>{row.values.phone ?? row.values.email ?? "—"}</td>
                <td>{row.errors.length > 0 ? row.errors.join(" ") : row.duplicateCandidates.length > 0 ? "Duplicado detectado" : "Lista para crear"}</td>
                <td>{row.duplicateCandidates.length > 0
                  ? <select value={decision?.action === "update" ? "update:" + (decision.targetCustomerId ?? "") : "skip"} onChange={(event) => {
                      const [action, targetCustomerId] = event.target.value.split(":");
                      setImportDecisions((current) => ({
                        ...current,
                        [row.rowNumber]: {
                          action: action === "update" ? "update" : "skip",
                          targetCustomerId: targetCustomerId || row.duplicateCandidates[0]?.customerId || null,
                        },
                      }));
                    }}>
                      <option value="skip">Omitir duplicado</option>
                      {row.duplicateCandidates.map((candidate) => <option key={candidate.customerId} value={"update:" + candidate.customerId}>Actualizar {candidate.displayName ?? candidate.customerId}</option>)}
                    </select>
                  : row.errors.length > 0 ? "Corregir archivo" : "Crear"}</td>
              </tr>;
            })}</tbody>
          </table>
        </div>
        <div className="cd-crm-operation-panel__actions">
          {importPreview.errorReport && <button type="button" className="cd-btn-ghost" onClick={() => downloadBase64(importPreview.errorReport!.fileName, importPreview.errorReport!.mimeType, importPreview.errorReport!.contentBase64)}>Descargar reporte de errores</button>}
          <button type="button" className="cd-btn-primary" disabled={commandBusy || !importPreview.canCommit} onClick={() => void commitImport()}>Confirmar importación revisada</button>
        </div>
      </>}
      </div>
    </SalonsDrawer>}

    {error && <div className="cd-crm-state" role="alert">
      <p className="cd-crm-state__title cd-crm-state__title--error">No se pudo cargar el listado</p>
      <p className="cd-crm-state__text">{error}</p>
      <ActionButton variant="secondary" onClick={() => {
        if (authExpired) navigate("/login?returnTo=" + encodeURIComponent(window.location.pathname));
        else setReloadNonce((value) => value + 1);
      }}>{authExpired ? "Iniciar sesión" : "Reintentar"}</ActionButton>
    </div>}
    {!error && loading && <div className="cd-crm-state"><p className="cd-crm-state__title">Cargando clientes…</p></div>}

    {!error && !loading && snapshot && <div className="cd-crm-views">
      {view === "summary" && <div className="cd-crm-view">
        <div className="cd-crm-summary-grid">
          <div className="cd-crm-summary-section">
            <h2 className="cd-crm-summary-section__title">Oportunidades hoy</h2>
            <div className="cd-crm-opp-grid">
              <SummaryCard count={snapshot.counts.recover} title="A recuperar" description="Con historial registrado y sin próximo turno confirmado." tone="recover" onClick={() => openList("recover")} />
              <SummaryCard count={snapshot.counts.recurrent} title="Recurrentes" description="Clientes con dos o más turnos registrados." tone="recurrent" onClick={() => openList("recurrent")} />
              <SummaryCard count={snapshot.counts.withUpcoming} title="Con turno" description="Clientes con próxima reserva confirmada." tone="booking" onClick={() => openList("all")} />
              <SummaryCard count={snapshot.counts.noShowRisk} title="Inasistencias" description="Clientes cuyo historial actual contiene no-show." tone="urgent" onClick={() => openList("all")} />
              <SummaryCard count={snapshot.counts.newOrNoBooking} title="Nuevos / sin turno" description="Clientes sin historial consolidado o sin booking." tone="lead" onClick={() => openList("all")} />
            </div>
          </div>
        </div>
      </div>}

      {view === "list" && <div className="cd-crm-view">
        <CustomerTable snapshot={snapshot} onOpen={(customerId) => navigate(`${base}/customers/${customerId}`)} />
        {snapshot.sourceHasMore && <p className="cd-crm-meta">La fuente actual tiene más clientes que el límite operativo mostrado.</p>}
      </div>}

      {view === "segments" && <div className="cd-crm-view">
        <div className="cd-crm-pipeline">
          <button type="button" className="cd-crm-pipeline-col" onClick={() => openList("all")}>
            <span className="cd-crm-pipeline-col__head"><strong>Todos</strong><span>{snapshot.counts.all}</span></span>
            <p>Todos los clientes disponibles en Customer Core.</p>
          </button>
          <button type="button" className="cd-crm-pipeline-col" onClick={() => openList("recurrent")}>
            <span className="cd-crm-pipeline-col__head"><strong>Recurrentes</strong><span>{snapshot.counts.recurrent}</span></span>
            <p>Dos o más turnos registrados. Criterio explícito y verificable.</p>
          </button>
          <button type="button" className="cd-crm-pipeline-col" onClick={() => openList("recover")}>
            <span className="cd-crm-pipeline-col__head"><strong>A recuperar</strong><span>{snapshot.counts.recover}</span></span>
            <p>Historial registrado sin próximo turno confirmado; excluye clientes nuevos con booking pendiente.</p>
          </button>
        </div>
        <p className="cd-crm-meta">
          {snapshot.criteria.spendSortAvailable
            ? `Ventas habilita orden por gasto en ${snapshot.criteria.spendSortCurrency}; los importes salen del ledger canónico.`
            : snapshot.items.some((item) => item.salesByCurrency.length > 0)
              ? "Hay ventas en más de una moneda; ClickDialog no mezcla importes para ordenar clientes."
              : "Todavía no hay ventas activas para calcular gasto."}
        </p>
      </div>}
    </div>}
  </section>;
}

function Customer360View({
  organizationId,
  customerId,
  api,
  navigate,
  base,
  routeTab,
}: {
  organizationId: string;
  customerId: string;
  api: PlatformApiClient;
  navigate: (path: string) => void;
  base: string;
  routeTab: Customer360Tab;
}) {
  const [snapshot, setSnapshot] = useState<SalonCustomer360 | null>(null);
  const [tab, setTab] = useState<Customer360Tab>(routeTab);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadNonce, setReloadNonce] = useState(0);
  const [editOpen, setEditOpen] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [editDraft, setEditDraft] = useState({ displayName: "", phone: "", email: "", tags: "", preferences: "{}" });
  const [noteDraft, setNoteDraft] = useState("");
  const [mergeQuery, setMergeQuery] = useState("");
  const [mergeCandidates, setMergeCandidates] = useState<SalonCustomerListItem[]>([]);
  const [mergeTargetId, setMergeTargetId] = useState<string | null>(null);
  const [commandBusy, setCommandBusy] = useState(false);
  const [commandError, setCommandError] = useState<string | null>(null);

  useEffect(() => {
    setTab(routeTab);
  }, [routeTab]);

  useEffect(() => {
    let current = true;
    setLoading(true);
    setError(null);
    api.salonCustomer360(organizationId, customerId)
      .then((next) => { if (current) setSnapshot(next); })
      .catch((cause) => { if (current) setError(errorText(cause)); })
      .finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [api, organizationId, customerId, reloadNonce]);

  useEffect(() => {
    if (!snapshot) return;
    setEditDraft({
      displayName: snapshot.customer.displayName ?? "",
      phone: snapshot.customer.phone ?? "",
      email: snapshot.customer.email ?? "",
      tags: snapshot.customer.tags.join(", "),
      preferences: JSON.stringify(snapshot.customer.preferences, null, 2),
    });
    setNoteDraft(snapshot.customer.notes ?? "");
  }, [snapshot?.canonicalCustomerId, snapshot?.revision]);

  async function saveProfile() {
    if (!snapshot) return;
    setCommandBusy(true);
    setCommandError(null);
    try {
      await api.updateSalonCustomer(organizationId, snapshot.canonicalCustomerId, {
        expectedRevision: snapshot.revision,
        displayName: editDraft.displayName.trim() || "Cliente",
        phone: editDraft.phone.trim() || undefined,
        email: editDraft.email.trim() || null,
        tags: tagsFromText(editDraft.tags),
        preferences: preferencesFromText(editDraft.preferences),
      });
      setEditOpen(false);
      setReloadNonce((value) => value + 1);
    } catch (cause) {
      setCommandError(errorText(cause));
    } finally {
      setCommandBusy(false);
    }
  }

  async function saveNotes(nextNotes: string | null) {
    if (!snapshot) return;
    setCommandBusy(true);
    setCommandError(null);
    try {
      await api.updateSalonCustomer(organizationId, snapshot.canonicalCustomerId, {
        expectedRevision: snapshot.revision,
        notes: nextNotes,
      });
      setReloadNonce((value) => value + 1);
    } catch (cause) {
      setCommandError(errorText(cause));
    } finally {
      setCommandBusy(false);
    }
  }

  async function archiveCustomer() {
    if (!snapshot || !window.confirm("¿Archivar este cliente? El historial se conserva.")) return;
    setCommandBusy(true);
    setCommandError(null);
    try {
      await api.archiveSalonCustomer(organizationId, snapshot.canonicalCustomerId, snapshot.revision);
      navigate(base + "/customers");
    } catch (cause) {
      setCommandError(errorText(cause));
    } finally {
      setCommandBusy(false);
    }
  }

  async function searchMergeCandidates() {
    if (!snapshot || !mergeQuery.trim()) return;
    setCommandBusy(true);
    setCommandError(null);
    try {
      const result = await api.salonCustomers(organizationId, { q: mergeQuery.trim(), limit: 20 });
      setMergeCandidates(result.items.filter((item) => item.customer.id !== snapshot.canonicalCustomerId));
    } catch (cause) {
      setCommandError(errorText(cause));
    } finally {
      setCommandBusy(false);
    }
  }

  async function mergeCustomer() {
    if (!snapshot || !mergeTargetId) return;
    setCommandBusy(true);
    setCommandError(null);
    try {
      const target = await api.salonCustomer360(organizationId, mergeTargetId);
      await api.mergeSalonCustomer(organizationId, snapshot.canonicalCustomerId, {
        targetCustomerId: target.canonicalCustomerId,
        expectedSourceRevision: snapshot.revision,
        expectedTargetRevision: target.revision,
      });
      navigate(base + "/customers/" + target.canonicalCustomerId);
    } catch (cause) {
      setCommandError(errorText(cause));
    } finally {
      setCommandBusy(false);
    }
  }

  const timeline = useMemo(() => {
    if (!snapshot) return [];
    const appointments = snapshot.appointments.map((item) => ({
      key: `appointment:${item.id}`,
      at: item.startsAt,
      icon: "event",
      title: item.service?.name ?? "Turno",
      text: `${appointmentStatusLabel(item.status)} · ${dateTime(item.startsAt)}`,
    }));
    const conversations = snapshot.conversations.map((item) => ({
      key: `conversation:${item.id}`,
      at: item.latestAt ?? item.updatedAt,
      icon: "chat",
      title: channelLabel(item.channel),
      text: item.latestBody ?? `Conversación ${item.status}`,
    }));
    return [...appointments, ...conversations].sort((left, right) => Date.parse(right.at) - Date.parse(left.at));
  }, [snapshot]);

  if (loading) return <div className="cd-c360-state"><p className="cd-c360-state__title">Cargando ficha del cliente…</p></div>;
  if (error || !snapshot) return <div className="cd-c360-state"><p className="cd-c360-state__title cd-c360-state__title--error">No se pudo cargar la ficha del cliente.</p><p className="cd-c360-state__text">{error ?? "Cliente no disponible."}</p><button className="cd-btn-ghost" onClick={() => navigate(`${base}/customers`)}>Volver a Clientes</button></div>;

  const customer = snapshot.customer;
  const profileSegment = customer360Segment(snapshot.summary.commercialState);
  const tabs: ReadonlyArray<{ key: Customer360Tab; label: string }> = [
    { key: "summary", label: "Resumen" },
    { key: "appointments", label: "Turnos" },
    { key: "conversations", label: "Conversaciones" },
    { key: "sales", label: "Ventas" },
    { key: "profile", label: "Perfil" },
    { key: "activity", label: "Actividad" },
  ];

  return <section className="cd-c360-product" aria-label="Ficha Cliente 360">
    <div className="cd-c360-workspace">
      <div className="cd-c360-search">
        <button type="button" className="cd-c360-search__crm-link" onClick={() => navigate(`${base}/customers`)}>
          <SalonIcon name="arrow_back" />
          Volver a clientes
        </button>
      </div>

      <header className="cd-c360-profile">
        <div className="cd-c360-profile__identity">
          <div className="cd-c360-profile__avatar" aria-hidden="true">{initials(customer.displayName)}</div>
          <div>
            <div className="cd-c360-profile__name-row">
              <h1 className="cd-c360-profile__name">{customer.displayName ?? "Cliente sin nombre"}</h1>
              <span className={`cd-c360-profile__badge cd-c360-segment--${profileSegment.modifier}`}>{profileSegment.label}</span>
            </div>
            <p className="cd-c360-profile__meta">
              {[customer.phone, customer.email, snapshot.channels.map(channelLabel).join(" · ")].filter(Boolean).join(" · ") || "Sin identidades adicionales"}
            </p>
            <p className="cd-c360-profile__meta cd-c360-profile__meta--sub">Cliente desde {dateTime(customer.firstSeenAt)}</p>
            {customer.notes && <p className="cd-c360-profile__meta cd-c360-profile__notes"><SalonIcon name="sticky_note_2" />{customer.notes}</p>}
          </div>
        </div>
        <div className="cd-c360-profile__actions">
          <button type="button" className="cd-c360-profile__btn cd-c360-profile__btn--outline" onClick={() => navigate(base + "/agenda?customerId=" + encodeURIComponent(snapshot.canonicalCustomerId))}>
            <SalonIcon name="calendar_add_on" />
            Agenda
          </button>
          <button type="button" className="cd-c360-profile__btn cd-c360-profile__btn--outline" onClick={() => setEditOpen((value) => !value)}>
            <SalonIcon name="person" />
            Editar cliente
          </button>
          <button type="button" className="cd-c360-profile__btn cd-c360-profile__btn--outline" onClick={() => setMergeOpen((value) => !value)}>
            <SalonIcon name="hub" />
            Fusionar
          </button>
          <button type="button" className="cd-c360-profile__btn cd-c360-profile__btn--primary" onClick={() => {
            const conversation = snapshot.conversations[0];
            navigate(conversation ? base + "/conversations/" + encodeURIComponent(conversation.id) : base + "/conversations");
          }}>
            <SalonIcon name="chat" />
            Conversaciones
          </button>
        </div>
      </header>

      {commandError && <div className="cd-crm-command-feedback cd-crm-command-feedback--error">{commandError}</div>}

      {editOpen && <section className="cd-c360-operation-panel" aria-label="Editar cliente">
        <div className="cd-crm-form-grid">
          <label>Nombre<input value={editDraft.displayName} onChange={(event) => setEditDraft((current) => ({ ...current, displayName: event.target.value }))} /></label>
          <label>Teléfono<input value={editDraft.phone} onChange={(event) => setEditDraft((current) => ({ ...current, phone: event.target.value }))} /></label>
          <label>Email<input value={editDraft.email} onChange={(event) => setEditDraft((current) => ({ ...current, email: event.target.value }))} /></label>
          <label>Etiquetas<input value={editDraft.tags} onChange={(event) => setEditDraft((current) => ({ ...current, tags: event.target.value }))} /></label>
          <label className="cd-crm-form-grid__wide">Preferencias JSON<textarea value={editDraft.preferences} onChange={(event) => setEditDraft((current) => ({ ...current, preferences: event.target.value }))} /></label>
        </div>
        <div className="cd-crm-operation-panel__actions">
          <button type="button" className="cd-btn-ghost" onClick={() => setEditOpen(false)}>Cancelar</button>
          <button type="button" className="cd-btn-primary" disabled={commandBusy} onClick={() => void saveProfile()}>Guardar cambios</button>
        </div>
      </section>}

      {mergeOpen && <section className="cd-c360-operation-panel" aria-label="Fusionar clientes duplicados">
        <h3>Revisión explícita de duplicados</h3>
        <p>El cliente actual se archivará como alias. Turnos, conversaciones y ventas históricos no se reescriben.</p>
        <div className="cd-c360-merge-search">
          <input placeholder="Buscar cliente destino" value={mergeQuery} onChange={(event) => setMergeQuery(event.target.value)} />
          <button type="button" className="cd-btn-ghost" disabled={commandBusy} onClick={() => void searchMergeCandidates()}>Buscar</button>
        </div>
        <div className="cd-c360-merge-candidates">
          {mergeCandidates.map((item) => <button type="button" key={item.customer.id} className={mergeTargetId === item.customer.id ? "is-selected" : ""} onClick={() => setMergeTargetId(item.customer.id)}>
            <strong>{item.customer.displayName ?? "Cliente sin nombre"}</strong>
            <span>{item.customer.phone ?? item.customer.email ?? item.customer.id}</span>
          </button>)}
        </div>
        <div className="cd-crm-operation-panel__actions">
          <button type="button" className="cd-btn-ghost" onClick={() => setMergeOpen(false)}>Cancelar</button>
          <button type="button" className="cd-btn-primary" disabled={commandBusy || !mergeTargetId} onClick={() => void mergeCustomer()}>Fusionar en cliente destino</button>
        </div>
      </section>}

      <div className="cd-c360-kpis">
        <article className="cd-c360-kpi"><span>Turnos</span><strong>{snapshot.summary.totalAppointments ?? "—"}</strong></article>
        <article className="cd-c360-kpi"><span>Confirmados</span><strong>{snapshot.summary.confirmedAppointments ?? "—"}</strong></article>
        <article className="cd-c360-kpi"><span>No-show</span><strong>{snapshot.summary.noShowAppointments ?? "—"}</strong></article>
        <article className="cd-c360-kpi"><span>Ventas</span><strong>{snapshot.salesByCurrency.reduce((total, item) => total + item.saleCount, 0)}</strong></article>
      </div>
      {snapshot.salesByCurrency.length > 0 && <div className="cd-c360-overview-grid">
        {snapshot.salesByCurrency.map((item) => <article key={item.currency} className="cd-c360-info-card">
          <h3>Ventas · {item.currency}</h3>
          <p>Estimado: <strong>{money(item.estimatedAmountMinor, item.currency)}</strong></p>
          <p>Cobrado: <strong>{money(item.collectedAmountMinor, item.currency)}</strong></p>
          <p>Pendiente: <strong>{money(item.pendingAmountMinor, item.currency)}</strong></p>
        </article>)}
      </div>}

      <section className="cd-c360-reco" aria-label="Recomendación">
        <div className="cd-c360-reco__icon"><SalonIcon name="notifications_active" /></div>
        <div className="cd-c360-reco__text"><h2 className="cd-c360-reco__title">{snapshot.nextAction.label}</h2><p className="cd-c360-reco__reason">{snapshot.nextAction.reason}</p></div>
      </section>

      <nav className="cd-c360-tabs" aria-label="Secciones de ficha">
        {tabs.map((item) => <button
          type="button"
          key={item.key}
          className={`cd-c360-tabs__item${tab === item.key ? " cd-c360-tabs__item--active" : ""}`}
          onClick={() => {
            setTab(item.key);
            const suffix = item.key === "summary" ? "" : "/" + item.key;
            navigate(base + "/customers/" + encodeURIComponent(snapshot.canonicalCustomerId) + suffix);
          }}
        >{item.label}</button>)}
      </nav>

      <div className="cd-c360-tab-panels">
        {tab === "summary" && <div className="cd-c360-tab-panel cd-c360-tab-panel--active">
          <div className="cd-c360-overview-grid">
            <article className="cd-c360-info-card">
              <h3>Próximo turno</h3>
              {snapshot.nextAppointment
                ? <><strong>{snapshot.nextAppointment.service?.name ?? "Servicio"}</strong><p>{dateTime(snapshot.nextAppointment.startsAt)}</p></>
                : <p>Sin turno confirmado próximo.</p>}
            </article>
            <article className="cd-c360-info-card">
              <h3>Actividad</h3>
              <p>Última actividad: {dateTime(customer.lastSeenAt ?? snapshot.summary.lastAppointmentAt)}</p>
              <p>Canales: {snapshot.channels.length ? snapshot.channels.map(channelLabel).join(", ") : "—"}</p>
            </article>
          </div>
        </div>}

        {tab === "appointments" && <div className="cd-c360-tab-panel cd-c360-tab-panel--active">
          <div className="cd-c360-bookings">
            {snapshot.appointments.map((item) => <button type="button" key={item.id} className="cd-c360-booking-card cd-c360-owner-link" onClick={() => navigate(base + "/agenda?appointmentId=" + encodeURIComponent(item.id) + "&customerId=" + encodeURIComponent(snapshot.canonicalCustomerId) + "&startsAt=" + encodeURIComponent(item.startsAt))}>
              <div><strong>{item.service?.name ?? "Servicio"}</strong><span>{dateTime(item.startsAt)}</span></div>
              <span className="cd-c360-booking-status">{appointmentStatusLabel(item.status)}</span>
            </button>)}
            {snapshot.appointments.length === 0 && <p>Sin turnos registrados.</p>}
          </div>
        </div>}

        {tab === "conversations" && <div className="cd-c360-tab-panel cd-c360-tab-panel--active">
          <div className="cd-c360-conversations">
            {snapshot.conversations.map((item) => <button type="button" key={item.id} className="cd-c360-conversation-card cd-c360-owner-link" onClick={() => navigate(base + "/conversations/" + encodeURIComponent(item.id))}>
              <div><strong>{channelLabel(item.channel)}</strong><span>{item.status}</span></div>
              <p>{item.latestBody ?? "Sin mensajes"}</p>
              <time>{dateTime(item.latestAt ?? item.updatedAt)}</time>
            </button>)}
            {snapshot.conversations.length === 0 && <p>Sin conversaciones vinculadas.</p>}
          </div>
        </div>}

        {tab === "sales" && <div className="cd-c360-tab-panel cd-c360-tab-panel--active">
          <div className="cd-c360-overview-grid">
            {snapshot.salesByCurrency.map((item) => <article key={item.currency} className="cd-c360-info-card">
              <h3>Ventas · {item.currency}</h3>
              <p>Operaciones: <strong>{item.saleCount}</strong></p>
              <p>Estimado: <strong>{money(item.estimatedAmountMinor, item.currency)}</strong></p>
              <p>Cobrado: <strong>{money(item.collectedAmountMinor, item.currency)}</strong></p>
              <p>Pendiente: <strong>{money(item.pendingAmountMinor, item.currency)}</strong></p>
            </article>)}
            {snapshot.salesByCurrency.length === 0 && <p>Sin ventas vinculadas.</p>}
          </div>
          <button type="button" className="cd-btn-ghost" onClick={() => navigate(base + "/sales?customerId=" + encodeURIComponent(snapshot.canonicalCustomerId))}>Abrir Ventas</button>
        </div>}

        {tab === "profile" && <div className="cd-c360-tab-panel cd-c360-tab-panel--active">
          <div className="cd-c360-overview-grid">
            <article className="cd-c360-info-card">
              <h3>Datos de contacto</h3>
              <p>Teléfono: <strong>{customer.phone ?? "—"}</strong></p>
              <p>Email: <strong>{customer.email ?? "—"}</strong></p>
              <p>Canales: <strong>{snapshot.channels.length ? snapshot.channels.map(channelLabel).join(", ") : "—"}</strong></p>
            </article>
            <article className="cd-c360-info-card">
              <h3>Etiquetas y preferencias</h3>
              <p>Etiquetas: <strong>{customer.tags.length ? customer.tags.join(", ") : "—"}</strong></p>
              {Object.keys(customer.preferences).length === 0
                ? <p>Sin preferencias estructuradas.</p>
                : <ul className="cd-c360-preferences">
                    {Object.entries(customer.preferences).map(([key, value]) => <li key={key}><span>{key}</span><strong>{String(value ?? "—")}</strong></li>)}
                  </ul>}
            </article>
          </div>
          <div className="cd-c360-note-editor">
            <label>Notas internas del cliente
              <textarea value={noteDraft} onChange={(event) => setNoteDraft(event.target.value)} placeholder="Preferencias, contexto operativo, recordatorios internos…" />
            </label>
            <div className="cd-crm-operation-panel__actions">
              <button type="button" className="cd-btn-ghost" disabled={commandBusy || !snapshot.customer.notes} onClick={() => { setNoteDraft(""); void saveNotes(null); }}>Eliminar nota</button>
              <button type="button" className="cd-btn-primary" disabled={commandBusy} onClick={() => void saveNotes(noteDraft.trim() || null)}>Guardar nota</button>
            </div>
          </div>
        </div>}

        {tab === "activity" && <div className="cd-c360-tab-panel cd-c360-tab-panel--active">
          <div className="cd-c360-timeline-card">
            <h3 className="cd-c360-timeline-card__title">Actividad canónica</h3>
            <div className="cd-c360-timeline">
              {snapshot.activity.map((event) => <div key={event.commandId + ":" + event.customerId} className="cd-c360-timeline__item">
                <SalonIcon name="verified" className="cd-c360-timeline__icon" />
                <div><strong>{event.operation}</strong><p>Revisión {event.revision} · Customer {event.customerId}</p><time>{dateTime(event.occurredAt)}</time></div>
              </div>)}
              {timeline.map((event) => <div key={event.key} className="cd-c360-timeline__item">
                <SalonIcon name={event.icon} className="cd-c360-timeline__icon" />
                <div><strong>{event.title}</strong><p>{event.text}</p><time>{dateTime(event.at)}</time></div>
              </div>)}
              {snapshot.activity.length === 0 && timeline.length === 0 && <p>Sin actividad disponible.</p>}
            </div>
          </div>
          {snapshot.mergedSourceCustomerIds.length > 0 && <p className="cd-c360-meta">Historial agregado desde {snapshot.mergedSourceCustomerIds.length} cliente(s) fusionado(s), sin reescribir owners externos.</p>}
        </div>}

        <div className="cd-c360-danger-zone">
          <button type="button" className="cd-btn-ghost" disabled={commandBusy || snapshot.customer.status === "archived"} onClick={() => void archiveCustomer()}>
            Archivar cliente
          </button>
        </div>
      </div>

      <p className="cd-c360-meta"><span className="cd-c360-badge cd-c360-badge--live">Datos del negocio</span> · Turnos, conversaciones y Ventas provienen de owners canónicos actuales.</p>
    </div>
  </section>;
}

export function SalonsCustomers({
  organizationId,
  nestedPath,
  api,
  navigate,
  basePath,
}: {
  organizationId: string;
  nestedPath: string;
  api: PlatformApiClient;
  navigate: (path: string) => void;
  basePath?: string;
}) {
  const base = basePath ?? window.location.pathname.match(/^(\/o\/[^/]+\/salons)/)?.[1] ?? "";
  const parts = nestedPath.replace(/^\/+/, "").split("/").filter(Boolean);
  const rawCustomerId = parts[0] === "customers" ? parts[1] ?? null : null;
  const customerId = rawCustomerId && /^cd_cus_[A-Za-z0-9._:-]+$/.test(rawCustomerId) ? rawCustomerId : null;

  const routeView: CustomerView =
    !customerId && parts[1] === "list"
      ? "list"
      : !customerId && parts[1] === "segments"
        ? "segments"
        : "summary";

  const routeTab: Customer360Tab =
    customerId && parts[2] === "appointments"
      ? "appointments"
      : customerId && parts[2] === "conversations"
        ? "conversations"
        : customerId && parts[2] === "sales"
          ? "sales"
          : customerId && parts[2] === "profile"
            ? "profile"
            : customerId && parts[2] === "activity"
              ? "activity"
              : "summary";

  if (customerId) {
    return <div className="cd-salons-customer-page">
      <Customer360View
        organizationId={organizationId}
        customerId={customerId}
        api={api}
        navigate={navigate}
        base={base}
        routeTab={routeTab}
      />
    </div>;
  }

  return <CustomersWorkspace
    organizationId={organizationId}
    api={api}
    navigate={navigate}
    base={base}
    routeView={routeView}
  />;
}
